import {Box, CircularProgress, Typography} from '@mui/material';
import * as Plot from '@observablehq/plot';
import dayjs from 'dayjs';
import {useEffect, useRef} from 'react';
import {QuizSession} from '@cp/model';

interface StudyTimeChartProps {
  sessions: QuizSession[];
  loading: boolean;
}

export default function StudyTimeChart({sessions, loading}: StudyTimeChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loading || !containerRef.current) return;

    if (!sessions || sessions.length === 0) {
      containerRef.current.innerHTML = '';
      return;
    }

    // Group sessions by day for the last 30 days
    const dailyData = new Map<string, {sessions: number; questions: number; day: string}>();
    const thirtyDaysAgo = dayjs().subtract(30, 'days');

    sessions.forEach((session) => {
      if (session.endedAt) {
        const sessionDate = dayjs(session.endedAt);
        if (sessionDate.isAfter(thirtyDaysAgo)) {
          const dayKey = sessionDate.format('YYYY-MM-DD');
          const existing = dailyData.get(dayKey);
          if (existing) {
            existing.sessions += 1;
            existing.questions += session.questions.length;
          } else {
            dailyData.set(dayKey, {
              sessions: 1,
              questions: session.questions.length,
              day: sessionDate.format('MMM DD'),
            });
          }
        }
      }
    });

    // Fill in missing days with 0 values
    const chartData: Array<{day: string; fullDay: string; sessions: number; questions: number}> = [];
    for (let i = 29; i >= 0; i -= 1) {
      const date = dayjs().subtract(i, 'days');
      const dayKey = date.format('YYYY-MM-DD');
      const data = dailyData.get(dayKey);

      chartData.push({
        day: date.format('MMM DD'),
        fullDay: date.format('MMM DD, YYYY'),
        sessions: data?.sessions || 0,
        questions: data?.questions || 0,
      });
    }

    const plotOptions: Plot.PlotOptions = {
      marks: [
        Plot.gridY({stroke: '#f0f0f0', strokeDasharray: '3,3'}),
        Plot.barY(chartData, {
          x: 'day',
          y: 'sessions',
          fill: '#ed6c02',
          tip: {
            format: {
              x: (d: string) => {
                const data = chartData.find((item) => item.day === d);
                return data?.fullDay || d;
              },
              y: (d: number) => `${d} ${d === 1 ? 'session' : 'sessions'}`,
            },
          },
        }),
      ],
      x: {
        label: 'Last 30 Days',
        type: 'band',
      },
      y: {
        label: 'Sessions',
        type: 'linear',
      },
      marginTop: 20,
      marginRight: 30,
      marginBottom: 5,
      marginLeft: 20,
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    };

    const chart = Plot.plot(plotOptions);
    const container = containerRef.current;
    container.innerHTML = '';
    container.appendChild(chart);

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [sessions, loading]);

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300}}>
        <CircularProgress />
      </Box>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <Box sx={{textAlign: 'center', py: 4}}>
        <Typography variant="body1" color="text.secondary">
          No session data available yet. Complete some study sessions to see your activity!
        </Typography>
      </Box>
    );
  }

  return <Box ref={containerRef} sx={{height: 300}} />;
}
